import React, { FC, useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { toast } from 'react-toastify'
import { useUserInfo } from 'src/services'
import { CellularPayPrompt as Prompt, CellularPayResult, PromptView } from './types'
import { rememberRemindLater } from './remind-later'
import { usePromptView } from './use-prompt-view'
import { useKnownPrompt } from './use-known-prompt'
import { setCellularPayPromptActive } from './prompt-active'
import { useRefreshBillingCaches } from './refresh-billing-caches'
import { logInDev } from './log'
import { OwedDialog } from './owed-dialog'
import { PaymentDialog } from './payment-dialog'
import { GiveUpDialog } from './give-up-dialog'
import { RestoreBanner } from './restore-banner'
import { DueBanner } from './due-banner'
import { MemberLockDialog } from './member-lock-dialog'
import { isLocked } from './is-locked'
import { ShutOffBanner } from './shut-off-banner'
import { isShutOff, isSimOff, isSuspendedStage, payableSimStage } from './sim-status'
import { AchBanner } from './ach-banner'
import { isLockingRenewal } from './owed-description'
import { achStartedMessage, pendingAch } from './ach'
import { PW_GIVE_UP_CELLULAR } from '../../../apollo/api/paywall/mutations'
import { PW_GET_CELLULAR_PAY_PROMPT } from '../../../apollo/api/paywall/queries'
import { isHideFeature } from '../../../helpers/white-label-helper'
import { WhiteLabelFeaturesEnum } from '../../../helpers/white-label.enum'

export const PAYMENT_SUCCESS_MESSAGE = 'Payment received. Your service is active again.'
export const RESTORED_MESSAGE = 'Payment received, cellular service is back on.'
export const RENEWAL_PAID_MESSAGE = 'Payment received. Thank you.'

// A customer whose SIMs were switched off wants to hear they are coming back. A renewal
// our flow locked never lapsed, so "active again" would be wrong for it (SHA-973 P3).
// Keyed on the lock, which the paywall sends only while the renewal flow runs: with the
// P2 prompt reader on and the flow off, billKind is sent but P2's toast stays.
export const paymentSuccessMessage = (prompt: Prompt): string => {
  if (isSuspendedStage(payableSimStage(prompt))) return RESTORED_MESSAGE
  return isLockingRenewal(prompt) ? RENEWAL_PAID_MESSAGE : PAYMENT_SUCCESS_MESSAGE
}

const GIVE_UP_FAILED = 'The cellular connection was not given up'

interface CellularPayPromptProps {
  // The workspace being viewed. Its owner's back-bill locks everyone working in it,
  // not just the owner; the paywall decides whether the viewer is one of them.
  workspaceId?: string
}

export const CellularPayPrompt: FC<CellularPayPromptProps> = ({ workspaceId }) => {
  const userInfo = useUserInfo()
  const accountId: string | undefined = userInfo?.me?.account?.id
  // White-label tenants never see ScreenHub billing — same rule as the old banner.
  const isHidePaywall = isHideFeature(WhiteLabelFeaturesEnum.PAYWALL)
  // An admin impersonating the customer sees exactly what the customer sees —
  // the lock and the working buttons included — so support can tell what is
  // happening to them (product, 2026-10-07).
  const skipPrompt = !accountId || isHidePaywall
  // Fail-open and quiet: apollo/links.ts skips the global error toast for this
  // operation by name (PwGetCellularPayPrompt); keep the two in step.
  const { data, error, refetch } = useQuery(PW_GET_CELLULAR_PAY_PROMPT, {
    skip: skipPrompt,
    variables: { accountId, workspaceId },
    fetchPolicy: 'network-only'
  })
  const [giveUpCellular] = useMutation(PW_GIVE_UP_CELLULAR)
  const refreshBillingCaches = useRefreshBillingCaches(accountId)
  // Every kind of cellular bill gets a banner here; only the back-bill catch-up
  // opens a dialog by itself (see usePromptView). A renewal customer who clicks
  // the banner still gets the pay dialog — what they do not get is a pop-up
  // appearing over their work unasked.
  const answer: Prompt | null | undefined =
    error || skipPrompt ? undefined : data?.getCellularPayPrompt
  // True from the moment a payment or give-up succeeds until the refreshed prompt
  // arrives, so a "Payment received" toast never sits next to "payment due".
  const [refreshing, setRefreshing] = useState(false)
  const [paymentReturn, setPaymentReturn] = useState<PromptView>('closed')

  const refresh = () => {
    if (skipPrompt) return Promise.resolve()
    return refetch().catch(refetchError =>
      logInDev('Failed to refresh the cellular pay prompt', refetchError)
    )
  }

  const known = useKnownPrompt({
    key: `${accountId}:${workspaceId}`,
    answer,
    failed: Boolean(error) && !skipPrompt,
    retry: refresh
  })
  const prompt: Prompt | null = skipPrompt ? null : known.prompt

  const { view, setView } = usePromptView({ prompt, refresh })

  useEffect(() => {
    if (error) logInDev('Failed to load the cellular pay prompt', error)
  }, [error])

  // The old CellularSubscriptionBanner's "CLICK HERE to renew" starts a second
  // subscription, so it stays down until this prompt has answered "nothing owed":
  // while it has a bill, while it is loading or failing, and while it refreshes
  // after a payment. Only where the prompt is never asked by design (white-label)
  // is the old banner left to itself.
  const neverAsked = isHidePaywall
  const bannerStandsDown = !neverAsked && (refreshing || !known.answered || Boolean(prompt))
  useEffect(() => {
    setCellularPayPromptActive(bannerStandsDown)
    return () => setCellularPayPromptActive(false)
  }, [bannerStandsDown])

  if (!prompt || !accountId || refreshing) return null

  // Every SIM on the bill is shut off for good: say so, lock nothing, offer
  // nothing to pay — for the owner and for anyone in their workspace alike.
  if (isShutOff(prompt)) return <ShutOffBanner prompt={prompt} />

  // The owner's bill, shown to someone working in their workspace: locked, but only
  // the owner can pay or give it up, so there is nothing here to click. A bill that
  // locks nothing is the owner's business alone: the member sees nothing of it.
  if (prompt.viewerIsOwner === false) {
    if (!isLocked(prompt)) return null
    return (
      <MemberLockDialog
        prompt={prompt}
        refresh={refresh}
        workspaceId={workspaceId}
      />
    )
  }

  // Everything the customer sees here is stale now: show nothing until the server
  // says what is left to pay, and let the older billing caches catch up too.
  const refreshAfterChange = async () => {
    known.forget()
    setRefreshing(true)
    await refresh()
    await refreshBillingCaches()
    setRefreshing(false)
  }

  const handlePaid = () => {
    setView('closed')
    toast.success(paymentSuccessMessage(prompt))
    refreshAfterChange()
  }

  // A bank payment takes days: the bill counts as paid for now, and the refreshed
  // prompt (no longer locking) shows the banner that says so.
  const handleBankStarted = (result: CellularPayResult) => {
    setView('closed')
    toast.info(achStartedMessage(result, isSimOff(prompt)))
    refreshAfterChange()
  }

  const handleRemindLater = () => {
    rememberRemindLater(prompt.invoiceId)
    setView('closed')
  }

  const handleGiveUp = async () => {
    const { data: result } = await giveUpCellular({
      // The bill on screen: the soonest Stripe due date can be another one.
      variables: {
        accountId,
        stripeSubscriptionId: prompt.stripeSubscriptionId,
        invoiceId: prompt.invoiceId
      }
    })
    // { ok: false } used to close the dialog and tell the customer it was done.
    if (!result?.giveUpCellular?.ok) throw new Error(GIVE_UP_FAILED)
    setView('closed')
    await refreshAfterChange()
  }

  // Back goes to wherever Pay was pressed: a banner, or the owed dialog. A locked
  // bill never goes back to a banner (usePromptView keeps it on the owed dialog).
  const openPayment = (from: PromptView) => {
    setPaymentReturn(from)
    setView('payment')
  }
  const back = () => setView(paymentReturn)

  return (
    <>
      {prompt.gaveUp && (
        <RestoreBanner
          onPay={() => openPayment('closed')}
          prompt={prompt}
        />
      )}
      {!prompt.gaveUp && pendingAch(prompt) && <AchBanner prompt={prompt} />}
      {!prompt.gaveUp && !pendingAch(prompt) && view === 'closed' && (
        <DueBanner
          onDetails={() => setView('owed')}
          onPay={() => openPayment('closed')}
          prompt={prompt}
        />
      )}
      <OwedDialog
        onGiveUp={() => setView('giveUp')}
        onPay={() => openPayment('owed')}
        onRemindLater={handleRemindLater}
        open={view === 'owed'}
        prompt={prompt}
      />
      {view === 'payment' && (
        <PaymentDialog
          accountId={accountId}
          onBack={back}
          onBankStarted={handleBankStarted}
          onCardSaved={refresh}
          onPaid={handlePaid}
          onRefresh={refresh}
          open
          prompt={prompt}
        />
      )}
      <GiveUpDialog
        onConfirm={handleGiveUp}
        onKeep={() => setView('owed')}
        open={view === 'giveUp'}
        prompt={prompt}
      />
    </>
  )
}
